import React, { useState } from "react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

// ============================================================================
// GAZEAL ELITE — What Agents Say
// Adapted from Gazeal Advance with gold accent (#FFDE59 → #FF914D).
// "Success Stories" pill removed per brief.
// ============================================================================

const FEATURED_QUOTE = {
  name: "Sean Barrett",
  role: "Fine & Country Surrey",
  quote:
  "Gazeal's Reservation Agreement system represents the most significant advancement in property transaction security we've seen in decades. It's not just about protecting commission, it's about bringing certainty to an industry plagued by uncertainty.",
  image:
  "https://customer-assets.emergentagent.com/job_bfedec72-4b0a-40b7-bc77-158aaceb834b/artifacts/0oz44sre_Sean%20Barrett.jpg"
};

const TESTIMONIALS = [
{
  id: 1,
  name: "Shaun Adams",
  role: "Owner, Cooper Adams",
  quote: "We list over 90% of our sales with a reservation agreement.",
  rating: 5
},
{
  id: 2,
  name: "Alex Knight",
  role: "Director, Knight Edmonds",
  quote: "If you ask me when is the right time to introduce a reservation agreement, my answer is, Always!",
  rating: 5
},
{
  id: 3,
  name: "Jack Flower",
  role: "Agent, eXp",
  quote: "We are Estate Agents who use Gazeal on all our deals. Highly recommend, great service and a beneficial product to both buyers and sellers.",
  rating: 5
},
{
  id: 4,
  name: "Chris Ellis",
  role: "Owner, Harrisons Homes",
  quote: "Reservation Agreements are saving me £100K a year and I have been using Gazeal from 2020.",
  rating: 5
},
{
  id: 5,
  name: "Billy Gill",
  role: "Area Director, Balgores",
  quote: "All my sellers will do it, otherwise, I WON'T TAKE THE LISTING.",
  rating: 5
}];


export default function EliteTestimonials() {
  const [activeIndex, setActiveIndex] = useState(0);
  const next = () => setActiveIndex((prev) => (prev + 1) % TESTIMONIALS.length);
  const prev = () => setActiveIndex((prev) => (prev - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  const t = TESTIMONIALS[activeIndex];

  return (
    <section
      id="testimonials"
      className="relative py-8 md:py-12 bg-black overflow-hidden"
      data-testid="elite-testimonials-section">

      {/* Ambient gold glow */}
      <div className="pointer-events-none absolute left-1/4 top-0 h-[360px] w-[520px] bg-gradient-to-b from-[#FF914D]/10 to-transparent blur-3xl opacity-50" />

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        {/* Heading (pill removed per brief) */}
        <div className="text-center mb-10 md:mb-12">
          <h2 className="font-didot italic text-3xl sm:text-4xl lg:text-[48px] leading-[1.15]">
            <span className="text-white">What Agents </span>
            <span className="bg-gradient-to-r from-[#FFDE59] to-[#FF914D] bg-clip-text text-transparent">Say</span>
          </h2>
        </div>

        {/* Featured Quote — Sean Barrett */}
        <div className="mb-12 md:mb-16">
          <div
            className="relative p-6 md:p-10 bg-gradient-to-r from-[#FF914D]/10 via-[#FFDE59]/[0.04] to-transparent border border-[#FF914D]/20 rounded-2xl overflow-hidden"
            data-testid="elite-featured-quote">

            <Quote className="absolute top-4 left-4 w-12 h-12 text-[#FF914D]/15" />

            <div className="flex flex-col md:flex-row gap-6 md:gap-10 items-center relative z-10">
              <div className="flex-shrink-0">
                <div className="relative">
                  <div className="w-28 h-28 md:w-36 md:h-36 overflow-hidden rounded-2xl ring-4 ring-[#FF914D]/30">
                    <img
                      src={FEATURED_QUOTE.image}
                      alt={FEATURED_QUOTE.name}
                      className="w-full h-full object-cover object-top" />

                  </div>
                  <div className="absolute inset-0 rounded-2xl ring-4 ring-[#FFDE59]/40 animate-pulse" />
                </div>
              </div>

              <div className="flex-1 text-center md:text-left">
                <blockquote className="text-lg md:text-xl lg:text-2xl text-white/90 leading-relaxed mb-4 font-didot italic">
                  "{FEATURED_QUOTE.quote}"
                </blockquote>
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                  <div className="text-white font-semibold text-lg font-manrope">{FEATURED_QUOTE.name}</div>
                  <div className="hidden md:block w-1.5 h-1.5 bg-gradient-to-br from-[#FFDE59] to-[#FF914D] rounded-full" />
                  <div className="text-[#FFDE59] text-sm font-manrope">{FEATURED_QUOTE.role}</div>
                </div>
              </div>
            </div>

            <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-[#FFDE59]/10 to-transparent" />
          </div>
        </div>

        {/* Agent Carousel */}
        <div className="max-w-3xl mx-auto">
          <div
            className="p-6 md:p-8 bg-white/[0.02] border border-white/[0.06] rounded-2xl text-center relative"
            data-testid="elite-testimonial-card">

            <div className="flex justify-center gap-1 mb-4">
              {[...Array(t.rating)].map((_, i) =>
              <Star key={i} className="w-4 h-4 text-[#FFDE59]" fill="currentColor" />
              )}
            </div>

            <blockquote className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 font-manrope">
              "{t.quote}"
            </blockquote>

            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#FFDE59]/20 to-[#FF914D]/20 border border-[#FF914D]/30 rounded-xl flex items-center justify-center">
                <span className="bg-gradient-to-r from-[#FFDE59] to-[#FF914D] bg-clip-text text-transparent font-bold text-sm font-manrope">
                  {t.name.split(" ").map((n) => n[0]).join("")}
                </span>
              </div>
              <div className="text-left">
                <div className="text-white font-semibold text-sm font-manrope">{t.name}</div>
                <div className="text-white/50 text-xs font-manrope">{t.role}</div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-3 mt-6">
            <button
              onClick={prev}
              className="w-9 h-9 border border-white/15 rounded-lg flex items-center justify-center hover:border-[#FF914D] hover:bg-[#FF914D]/10 transition-colors"
              aria-label="Previous testimonial"
              data-testid="elite-prev-testimonial">

              <ChevronLeft className="w-4 h-4 text-white" />
            </button>
            <div className="flex gap-1.5">
              {TESTIMONIALS.map((_, i) =>
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                className={`h-2 rounded-full transition-all ${
                i === activeIndex ?
                "bg-gradient-to-r from-[#FFDE59] to-[#FF914D] w-6" :
                "bg-white/25 w-2 hover:bg-white/45"}`
                }
                aria-label={`Go to testimonial ${i + 1}`} />

              )}
            </div>
            <button
              onClick={next}
              className="w-9 h-9 border border-white/15 rounded-lg flex items-center justify-center hover:border-[#FF914D] hover:bg-[#FF914D]/10 transition-colors"
              aria-label="Next testimonial"
              data-testid="elite-next-testimonial">

              <ChevronRight className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      </div>
    </section>);

}
